import { get, noop } from 'lodash';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState, Children } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { url as urlUtils } from '@clubmed/utils';
import { Section } from 'Components/frameworks/section/Section';
import { Scrollable } from 'Components/frameworks/scrollable/Scrollable';
import { NavTag } from 'Components/frameworks/nav-tag/NavTag';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
type Props = {
  children: Array<Node>;
  className?: string;
  navItems: Array<string>;
  pageType: string;
  getTrackingData?: (arg0: string) => TrackingData;
  scrollBackwardLabel?: string;
  scrollForwardLabel?: string;
};
export const Sections = ({
  children,
  className,
  navItems,
  getTrackingData,
  scrollBackwardLabel,
  scrollForwardLabel,
  pageType,
}: Props) => {
  const track = useTracking();
  const navBarRef = useRef(null);
  const onSectionIntersectViewport = useRef<(arg0: any) => any>(noop);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navSelector = pageType || 'Sections';
  // eslint-disable-next-line no-unused-vars
  const [isSticky, setIsSticky] = useState(false);
  const onNavItemClick = useCallback((anchor: string, index: number, label: string) => {
    return (e: React.SyntheticEvent) => {
      e.preventDefault();
      const selected = Number(index);
      const scrollRootOffsetSelector = `#${navSelector}`;

      if (getTrackingData) {
        track(getTrackingData(label));
      }

      scrollToElement(`#${anchor}`, 600, scrollRootOffsetSelector).then(() => {
        setSelectedIndex(selected);
      });
    };
  }, []);
  const navbarItems = useMemo(() => {
    return navItems.map((label, index) => {
      const anchor = label ? urlUtils.normalizeSlug(label) : '';
      return {
        href: `#${anchor}`,
        label,
        onClick: anchor ? onNavItemClick(anchor, index, label) : noop,
      };
    });
  }, [navItems]);
  useEffect(() => {
    if (!isSticky) {
      setSelectedIndex(0);
    }
  }, [isSticky]);
  useEffect(() => {
    onSectionIntersectViewport.current = (event: any) => {
      const visiblePercentage = Math.floor(event.intersectionRatio * 100);
      const navBarHeight = navBarRef && get(navBarRef, 'current.offsetHeight');
      const selected = Number(event.target.dataset.index);

      if (
        event.isIntersecting &&
        visiblePercentage > 10 &&
        event.intersectionRect.top <= navBarHeight
      ) {
        setSelectedIndex(selected);
      }
    };
  }, []);
  useEffect(() => {
    const element = navBarRef.current;
    const observer = new IntersectionObserver(([e]) => setIsSticky(e.intersectionRatio < 1), {
      threshold: [1],
    });

    if (element) {
      observer.observe(element);
      return () => observer.unobserve(element);
    }
  }, [navBarRef]);
  return (
    <div className={className}>
      <div
        ref={navBarRef}
        id={navSelector}
        className={classnames('sticky -top-1 mx-auto flex w-full flex-wrap bg-white', {
          'z-8 border-b-1': isSticky,
        })}
      >
        <Scrollable
          backwardLabel={scrollBackwardLabel}
          childrenWrapperClassNames="flex gap-10"
          className="w-full max-w-site bg-white py-10 lg:mx-auto"
          forceDisplayControls
          forwardLabel={scrollForwardLabel}
        >
          {navbarItems.map((item, index) => (
            <NavTag
              href={item.href}
              isActive={index === selectedIndex}
              key={item.label}
              onClick={item.onClick}
            >
              {item.label}
            </NavTag>
          ))}
        </Scrollable>
      </div>
      {Children.map(children, (section, index) => (
        <Section
          index={index}
          key={navItems[index]}
          name={navItems[index]}
          onSectionIntersectViewport={onSectionIntersectViewport.current}
        >
          {section}
        </Section>
      ))}
    </div>
  );
};
Sections.defaultProps = {
  children: [],
  navItems: [],
  pageType: '',
};
